const SURE_TILE_RE = /^s_[^/]+\.(json|glb)$/i;
export const TRIMBLE_HOST = 'fileservice.trimblecloud.com';

/** Strip SURE folder prefixes; flat Trimble storage uses filename only. */
export function fixSureFlatPath(path: string): string {
  const raw = String(path ?? '');

  // entry/ is only stripped when the child is a flat SURE splat tile (s_*.json|glb).
  if (raw.startsWith('entry/')) {
    const afterEntry = raw.slice('entry/'.length);
    const baseName = afterEntry.split('/').pop() ?? afterEntry;
    if (!afterEntry.includes('/') && SURE_TILE_RE.test(baseName)) {
      return baseName;
    }
    return raw;
  }

  const fixed = raw
    .replace(/^\.\.\/L\d+\/[A-Z]{2}\//, '')
    .replace(/^\.\.\//, '')
    .replace(/^fs_s\/L\d+\/[A-Z]{2}\//, '')
    .replace(/^L\d+\/[A-Z]{2}\//, '');

  // Hierarchical tilesets keep folder-relative URIs (e.g. 0227_4381/entry_relative/s_*.json).
  if (fixed.includes('/')) {
    return fixed;
  }

  const base = fixed.split('/').pop() ?? fixed;
  if (SURE_TILE_RE.test(base)) {
    return base;
  }

  return fixed;
}

function tilesetBasePath(tilesetUrl: string): string {
  const tileset = new URL(tilesetUrl);
  return tileset.pathname.replace(/\/[^/]*$/, '/');
}

function tilePathFromUrl(requestUrl: string, tilesetUrl: string): string {
  const resolved = new URL(requestUrl, tilesetUrl);
  const basePath = tilesetBasePath(tilesetUrl);
  let path = resolved.pathname;
  if (path.startsWith(basePath)) {
    path = path.slice(basePath.length);
  }
  path = path.replace(/^\//, '');
  if (!path || path.includes('..')) {
    path = resolved.pathname.split('/').pop() ?? path;
  }
  return fixSureFlatPath(path);
}

/** `new URL` throws TypeError for malformed input; anything else is a bug. */
function rethrowUnlessInvalidUrl(error: unknown): void {
  if (!(error instanceof TypeError)) {
    throw error;
  }
}

export function isRequestForTileset(
  requestUrl: string,
  tilesetUrl: string
): boolean {
  try {
    const tileset = new URL(tilesetUrl);
    const request = new URL(requestUrl, tilesetUrl);
    return (
      request.origin === tileset.origin &&
      request.pathname.startsWith(tilesetBasePath(tilesetUrl))
    );
  } catch (error: unknown) {
    rethrowUnlessInvalidUrl(error);
    return false;
  }
}

export function isTilesetJsonRequest(url: string, tilesetUrl: string): boolean {
  if (!url.includes(TRIMBLE_HOST) || !isRequestForTileset(url, tilesetUrl)) {
    return false;
  }

  try {
    return new URL(url).pathname.endsWith('.json');
  } catch (error: unknown) {
    rethrowUnlessInvalidUrl(error);
    return false;
  }
}

/**
 * Rewrite a Trimble FileService tile request to a signed flat-path URL.
 */
export function rewriteTrimbleTileUrl(
  requestUrl: string,
  tilesetUrl: string,
  templateUrl?: string
): string {
  if (!requestUrl.includes(TRIMBLE_HOST)) {
    return requestUrl;
  }

  if (!isRequestForTileset(requestUrl, tilesetUrl)) {
    return requestUrl;
  }

  try {
    const path = tilePathFromUrl(requestUrl, tilesetUrl);

    if (templateUrl?.includes('{path}')) {
      return templateUrl.replace('{path}', path);
    }

    const tileset = new URL(tilesetUrl);
    const target = new URL(tilesetUrl);
    target.pathname = tilesetBasePath(tilesetUrl) + path;
    target.search = tileset.search;
    return target.toString();
  } catch (error: unknown) {
    rethrowUnlessInvalidUrl(error);
    return requestUrl;
  }
}
